import { FC, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import AnnuityCalculator from "../AnnuityCalculator";
import theme from "../theme";
import { translate } from "../translations";
import { parseBoolean, parseNumber, parseText } from "../utils";
import CTA from "../components/CTA";

/*
?title=Lánroknari
&subtitle=Rokna mánaðarliga gjaldið, eisini eftir rentustuðul, niðanfyri
&showTitle=true
&showAccruals=false
&showPayments=false
&showInflation=false
&showSubsidyAmount=false
&showPaymentAfterSubsidy=false
&titleColor=%23921628
&subtitleColor=%23000000
&sliderColor=%23921628
&interestSubsidy=0.35
&interestSubsidyLimit=100000
&amountMin=0
&amountMax=5000000
&amountIncrement=10000
&amountDefault=1000000
&durationMin=12
&durationMax=360
&durationIncrement=12
&durationDefault=240
&interestMin=0
&interestMax=10
&interestIncrement=0.05
&interestDefault=4.5
&accrualsMin=1
&accrualsMax=12
&accrualsIncrement=1
&accrualsDefault=4
&paymentsMin=1
&paymentsMax=12
&paymentsIncrement=1
&paymentsDefault=12
&inflationMin=0
&inflationMax=10
&inflationIncrement=0.05
&inflationDefault=0
*/

// title: String - The title of the page.
// subtitle: String - The subtitle of the page.
// showTitle: Boolean - Indicates if the title should be displayed.
// showAccruals: Boolean - Indicates if accruals should be visible to the user.
// showPayments: Boolean - Indicates if payments should be visible to the user.
// showInflation: Boolean - Indicates if inflation details should be visible to the user.
// showSubsidyAmount: Boolean - Indicates if the subsidy amount should be visible to the user.
// showPaymentAfterSubsidy: Boolean - Indicates if the payment amount after subsidy should be visible to the user.
// titleColor: String (hex) - The hexadecimal color code for the title text.
// subtitleColor: String (hex) - The hexadecimal color code for the subtitle text.
// sliderColor: String (hex) - The hexadecimal color code for the slider UI.
// interestSubsidy: Number - The percentage of the interest that is subsidized.
// interestSubsidyLimit: Number - The maximum interest cost that is subsidized.
// amountMin: Number - Minimum loan amount in DKK.
// amountMax: Number - Maximum loan amount in DKK.
// amountIncrement: Number - Incremental step for the loan amount in DKK.
// amountDefault: Number - Default loan amount in DKK.
// durationMin: Number - Minimum loan duration in months.
// durationMax: Number - Maximum loan duration in months.
// durationIncrement: Number - Incremental step for loan duration in months.
// durationDefault: Number - Default loan duration in months.
// interestMin: Number - Minimum loan interest rate in percentage.
// interestMax: Number - Maximum loan interest rate in percentage.
// interestIncrement: Number - Incremental step for the loan interest rate in percentage.
// interestDefault: Number - Default loan interest rate in percentage.
// accrualsMin: Number - Minimum number of accruals per year.
// accrualsMax: Number - Maximum number of accruals per year.
// accrualsIncrement: Number - Incremental step for the number of accruals per year.
// accrualsDefault: Number - Default number of accruals per year.
// paymentsMin: Number - Minimum number of payments per year.
// paymentsMax: Number - Maximum number of payments per year.
// paymentsIncrement: Number - Incremental step for the number of payments per year.
// paymentsDefault: Number - Default number of payments per year.
// inflationMin: Number - Minimum inflation rate in percentage.
// inflationMax: Number - Maximum inflation rate in percentage.
// inflationIncrement: Number - Incremental step for the inflation rate in percentage.
// inflationDefault: Number - Default inflation rate in percentage.

// https://ss-calculator.web.app?title=BústaðarLán&subtitle=Rokna+mánaðarliga+gjaldið%2C+eisini+eftir+rentustuðul%2C+niðanfyri&showTitle=true&showAccruals=false&showPayments=false&showInflation=false&showSubsidyAmount=false&showPaymentAfterSubsidy=false&titleColor=%23921628&subtitleColor=%23000000&sliderColor=%23921628&interestSubsidy=0.35&interestSubsidyLimit=100000&amountMin=0&amountMax=5000000&amountIncrement=10000&amountDefault=1000000&durationMin=12&durationMax=360&durationIncrement=12&durationDefault=240&interestMin=0&interestMax=10&interestIncrement=0.05&interestDefault=4.5&accrualsMin=1&accrualsMax=12&accrualsIncrement=1&accrualsDefault=4&paymentsMin=1&paymentsMax=12&paymentsIncrement=1&paymentsDefault=12&inflationMin=0&inflationMax=10&inflationIncrement=0.05&inflationDefault=0

const AnnuityCalculatorPage: FC = () => {
  const [searchParams] = useSearchParams();

  // Display options
  const showTitle = parseBoolean(searchParams.get("showTitle"), true);
  const showAccruals = parseBoolean(searchParams.get("showAccruals"), false);
  const showPayments = parseBoolean(searchParams.get("showPayments"), false);
  const showInflation = parseBoolean(searchParams.get("showInflation"), false);
  const showSubsidyAmount = parseBoolean(
    searchParams.get("showSubsidyAmount"),
    false
  );
  const showPaymentAfterSubsidy = parseBoolean(
    searchParams.get("showPaymentAfterSubsidy"),
    false
  );

  // Text
  const title = parseText(
    searchParams.get("title"),
    translate("titleAnnuity") as string
  );
  const subtitle = parseText(searchParams.get("subtitle"), "");

  // Colors
  const titleColor = parseText(
    searchParams.get("titleColor"),
    theme.color.wineRed
  );
  const subtitleColor = parseText(
    searchParams.get("subtitleColor"),
    theme.color.black
  );
  const sliderColor = parseText(
    searchParams.get("sliderColor"),
    theme.color.wineRed
  );

  // Amount
  const amountMin = parseNumber(searchParams.get("amountMin"), 0);
  const amountMax = parseNumber(searchParams.get("amountMax"), 5_000_000);
  const amountDefault = parseNumber(
    searchParams.get("amountDefault"),
    1_000_000
  );
  const amountIncrement = parseNumber(
    searchParams.get("amountIncrement"),
    10_000
  );

  // Interest
  const interestMin = parseNumber(searchParams.get("interestMin"), 0);
  const interestMax = parseNumber(searchParams.get("interestMax"), 10);
  const interestDefault = parseNumber(searchParams.get("interestDefault"), 4.5);
  const interestIncrement = parseNumber(
    searchParams.get("interestIncrement"),
    0.05
  );

  // Duration
  const durationMin = parseNumber(searchParams.get("durationMin"), 12 * 1);
  const durationMax = parseNumber(searchParams.get("durationMax"), 30 * 12);
  const durationDefault = parseNumber(
    searchParams.get("durationDefault"),
    20 * 12
  );
  const durationIncrement = parseNumber(
    searchParams.get("durationIncrement"),
    12
  );

  // Accruals
  const accrualsMin = parseNumber(searchParams.get("accrualsMin"), 1);
  const accrualsMax = parseNumber(searchParams.get("accrualsMax"), 12);
  const accrualsDefault = parseNumber(searchParams.get("accrualsDefault"), 4);
  const accrualsIncrement = parseNumber(
    searchParams.get("accrualsIncrement"),
    1
  );

  // Payments
  const paymentsMin = parseNumber(searchParams.get("paymentsMin"), 1);
  const paymentsMax = parseNumber(searchParams.get("paymentsMax"), 12);
  const paymentsDefault = parseNumber(searchParams.get("paymentsDefault"), 12);
  const paymentsIncrement = parseNumber(
    searchParams.get("paymentsIncrement"),
    1
  );

  // Inflation
  const inflationMin = parseNumber(searchParams.get("inflationMin"), 0);
  const inflationMax = parseNumber(searchParams.get("inflationMax"), 10);
  const inflationDefault = parseNumber(searchParams.get("inflationDefault"), 0);
  const inflationIncrement = parseNumber(
    searchParams.get("inflationIncrement"),
    0.05
  );

  // Interest subsidy
  const interestSubsidy = parseNumber(
    searchParams.get("interestSubsidy"),
    0.35
  );
  const interestSubsidyLimit = parseNumber(
    searchParams.get("interestSubsidyLimit"),
    100_000
  );

  useEffect(() => {
    const root = document.documentElement;

    root.style.setProperty("--main-bg-color", sliderColor);
  }, [sliderColor]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px",
        width: "100%",
        marginBottom: "80px",
      }}
    >
      {showTitle && (
        <h1
          style={{
            color: titleColor,
            margin: `${theme.space.huge} 0 0 0`,
            fontSize: theme.fontSize.huge,
            padding: "0 5%",
          }}
        >
          {title}
        </h1>
      )}
      {subtitle && (
        <p
          style={{
            color: subtitleColor,
            margin: "10px 0 0 0",
            padding: "0 5%",
          }}
        >
          {subtitle}
        </p>
      )}
      <AnnuityCalculator
        options={{
          amountMin,
          amountMax,
          amountIncrement,
          amountDefault,
          interestMin,
          interestMax,
          interestIncrement,
          interestDefault,
          durationMin,
          durationMax,
          durationIncrement,
          durationDefault,
          accrualsMin,
          accrualsMax,
          accrualsIncrement,
          accrualsDefault,
          paymentsMin,
          paymentsMax,
          paymentsIncrement,
          paymentsDefault,
          inflationMin,
          inflationMax,
          inflationIncrement,
          inflationDefault,
          interestSubsidy,
          interestSubsidyLimit,
        }}
        showAccruals={showAccruals}
        showPayments={showPayments}
        showInflation={showInflation}
        showSubsidyAmount={showSubsidyAmount}
        showPaymentAfterSubsidy={showPaymentAfterSubsidy}
      />

      <CTA
        href="https://www.ss.fo/formularar/bilegg-prat-formular"
        rel="noreferrer"
        target="_blank"
      >
        {translate("bookMeeting")}
      </CTA>
    </div>
  );
};

export default AnnuityCalculatorPage;
